import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { getSlots, book, type Slot, type BookResult } from "../api";
import { SlotPicker } from "../components/SlotPicker";
import { Calendar } from "../components/Calendar";
import { BookingForm, type BookingFormData } from "../components/BookingForm";
import { Confirm } from "./Confirm";
import { getSession } from "../lib/session";
import { loadTimezone } from "../lib/timezones";

function todayInTz(tz: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  return fmt.format(new Date());
}

/** Fetch the day before/of/after the selected date (owner's calendar) and keep
 * only the slots that fall on `dateISO` once viewed in the client's chosen timezone. */
async function loadSlotsForClientDate(dateISO: string, tz: string): Promise<Slot[]> {
  const base = new Date(`${dateISO}T12:00:00Z`);
  const candidateDates = [-1, 0, 1].map((delta) => {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().slice(0, 10);
  });
  const results = await Promise.all(candidateDates.map((d) => getSlots(d).catch(() => [] as Slot[])));
  const all = results.flat();
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  return all.filter((s) => fmt.format(new Date(s.start)) === dateISO);
}

export function Booking() {
  const { t, lang } = useT();
  const [timezone] = useState(loadTimezone());
  const [date, setDate] = useState(() => todayInTz(timezone));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BookResult | null>(null);
  const [error, setError] = useState<"slotTaken" | "genericError" | null>(null);

  async function loadSlots(d: string) {
    setLoading(true);
    setError(null);
    try {
      setSlots(await loadSlotsForClientDate(d, timezone));
    } catch {
      setSlots([]);
      setError("genericError");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!picked && !result) void loadSlots(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  async function submit(data: BookingFormData) {
    if (!picked) return;
    setSubmitting(true);
    setError(null);
    try {
      const session = await getSession();
      if (!session) throw Object.assign(new Error("unauthorized"), { status: 401 });
      const r = await book({
        date: picked.start.slice(0, 10),
        start: picked.start,
        lang,
        accessToken: session.access_token,
        ...data,
      });
      setResult(r);
    } catch (e) {
      const status = (e as Error & { status?: number }).status;
      if (status === 409) {
        setError("slotTaken");
        setPicked(null);
        void loadSlots(date);
      } else {
        setError("genericError");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setResult(null);
    setPicked(null);
    void loadSlots(date);
  }

  if (result) return <Confirm result={result} onNew={reset} />;

  return (
    <div className="panel wide">
      <span className="eyebrow">{t("appTitle")}</span>
      <h2>{t("tagline")}</h2>

      {error && <div className="banner err">{t(error)}</div>}

      <div className="tzcurrent">
        <span className="muted">{t("bookingInTz")}</span> <span className="tzcurrent-static">{timezone}</span>
      </div>

      {!picked ? (
        <>
          <Calendar selected={date} minISO={todayInTz(timezone)} onSelect={setDate} />
          <div className="section-label">{t("pickTime")}</div>
          <SlotPicker slots={slots} loading={loading} timezone={timezone} onPick={setPicked} />
        </>
      ) : (
        <BookingForm submitting={submitting} onSubmit={submit} onBack={() => setPicked(null)} />
      )}
    </div>
  );
}
